import {
  onAuthStateChanged,
  signInWithEmailLink,
  isSignInWithEmailLink,
  signOut,
  type User,
} from "firebase/auth";
import { useCallback, useEffect, useState } from "react";

import { auth } from "@/lib/firebase";
import { apiFetch } from "@/lib/api";

const EMAIL_STORAGE_KEY = "misterpet:signin:email";

/** Lanciato da `completeFromLink` quando l'email non è in localStorage e non
 * viene passata esplicitamente. La UI deve gestirlo mostrando un input inline. */
export class EmailRequiredError extends Error {
  constructor() {
    super("Email richiesta per completare l'accesso.");
    this.name = "EmailRequiredError";
  }
}

export interface FirebaseAuthState {
  user: User | null;
  loading: boolean;
  /** Manda magic link all'email indicata. */
  sendLink: (email: string) => Promise<void>;
  /** Completa il login dal link aperto nell'URL corrente. */
  completeFromLink: (url: string, email?: string) => Promise<User>;
  /** Logout completo. */
  logout: () => Promise<void>;
  /** True se l'URL corrente è un magic link Firebase. */
  isLink: (url: string) => boolean;
}

export function useFirebaseAuth(): FirebaseAuthState {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const sendLink = useCallback(async (email: string) => {
    // Il link viene generato dal backend (Firebase Admin SDK) e mandato
    // via SendGrid con template HTML brandizzato. Bypass dei template Firebase
    // (body bloccato anti-phishing) per avere brand control completo.
    await apiFetch("/api/v1/auth/request-magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    // Salva l'email in localStorage; serve a `completeFromLink` quando l'utente
    // torna sul link (stesso browser, eventualmente tab diverso).
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  }, []);

  const isLink = useCallback((url: string) => isSignInWithEmailLink(auth, url), []);

  const completeFromLink = useCallback(async (url: string, email?: string) => {
    const resolvedEmail = email ?? window.localStorage.getItem(EMAIL_STORAGE_KEY) ?? "";
    if (!resolvedEmail) {
      throw new EmailRequiredError();
    }
    const result = await signInWithEmailLink(auth, resolvedEmail, url);
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  return { user, loading, sendLink, completeFromLink, logout, isLink };
}

/**
 * Recupera l'ID token corrente. Force refresh ad ogni chiamata
 * costa poco (Firebase SDK fa caching interno se non è scaduto).
 */
export async function getCurrentIdToken(forceRefresh = false): Promise<string | null> {
  const u = auth.currentUser;
  if (!u) return null;
  return u.getIdToken(forceRefresh);
}
